// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { parseActionResponseBody } from "@/services/graphqlActionError";
export var PASSWORD_POLICY_VIOLATION_ERROR_CODE = "PasswordPolicyViolation";
export var PASSWORD_POLICY_NOT_CONFIGURED_ERROR_CODE = "PasswordPolicyNotConfigured";
export var PASSWORD_POLICY_MINIMUM_LENGTH_MISSING_ERROR_CODE = "PasswordPolicyMinimumLengthMissing";
export var PASSWORD_POLICY_CHARACTER_CLASS_MISSING_ERROR_CODE = "PasswordPolicyCharacterClassMissing";
var passwordPolicyRules = [
    "minimumLength",
    "maximumLength",
    "uppercase",
    "lowercase",
    "digits",
    "specialCharacters",
];
var getViolationDetails = function (value) {
    var extensions = value;
    var rule = passwordPolicyRules.find(function (candidate) { return candidate === (extensions === null || extensions === void 0 ? void 0 : extensions.password_policy_rule); });
    var requiredCount = typeof (extensions === null || extensions === void 0 ? void 0 : extensions.password_policy_required_count) === "number"
        ? extensions.password_policy_required_count
        : undefined;
    return rule || requiredCount !== undefined ? { rule: rule, requiredCount: requiredCount } : undefined;
};
export var hasGraphQLActionErrorCode = function (error, errorCode, legacyMessages) {
    var _a;
    if (legacyMessages === void 0) { legacyMessages = []; }
    var actionError = error;
    var markers = __spreadArray([errorCode], legacyMessages, true);
    var containsMarker = function (value) {
        return markers.some(function (marker) { return (value === null || value === void 0 ? void 0 : value.includes(marker)) === true; });
    };
    return (((_a = actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) === null || _a === void 0 ? void 0 : _a.some(function (graphQLError) {
        var _a, _b, _c, _d;
        return ((_a = graphQLError.extensions) === null || _a === void 0 ? void 0 : _a.code) === errorCode ||
            containsMarker(graphQLError.message) ||
            containsMarker((_d = (_c = (_b = graphQLError.extensions) === null || _b === void 0 ? void 0 : _b.internal) === null || _c === void 0 ? void 0 : _c.response) === null || _d === void 0 ? void 0 : _d.body);
    })) === true || containsMarker(actionError === null || actionError === void 0 ? void 0 : actionError.message));
};
export var isPasswordPolicyViolationError = function (error) {
    return hasGraphQLActionErrorCode(error, PASSWORD_POLICY_VIOLATION_ERROR_CODE);
};
export var getPasswordPolicyViolation = function (error) {
    var _a, _b, _c, _d;
    if (!isPasswordPolicyViolationError(error)) {
        return undefined;
    }
    var actionError = error;
    for (var _i = 0, _e = (_a = actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) !== null && _a !== void 0 ? _a : []; _i < _e.length; _i++) {
        var graphQLError = _e[_i];
        var directDetails = getViolationDetails(graphQLError.extensions);
        if (directDetails) {
            return directDetails;
        }
        var responseBody = parseActionResponseBody((_d = (_c = (_b = graphQLError.extensions) === null || _b === void 0 ? void 0 : _b.internal) === null || _c === void 0 ? void 0 : _c.response) === null || _d === void 0 ? void 0 : _d.body);
        var responseDetails = getViolationDetails(responseBody === null || responseBody === void 0 ? void 0 : responseBody.extensions);
        if (responseDetails) {
            return responseDetails;
        }
    }
    return {};
};
export var isPasswordPolicyNotConfiguredError = function (error) {
    return hasGraphQLActionErrorCode(error, PASSWORD_POLICY_NOT_CONFIGURED_ERROR_CODE, [
        "Password Policy is not configured.",
    ]);
};
/**
 * The message a rejected password should read as, or undefined when the error
 * is not a policy violation. Shared so that the same backend rejection reads
 * the same way wherever a password is submitted.
 */
export var getPasswordPolicyMessage = function (error, t) {
    var violation = getPasswordPolicyViolation(error);
    if (!violation) {
        return undefined;
    }
    return violation.rule && violation.requiredCount !== undefined
        ? t("usersAndRolesScreen.editPassword.passwordPolicyRules.".concat(violation.rule), {
            count: violation.requiredCount,
        })
        : t("usersAndRolesScreen.editPassword.passwordPolicyViolation");
};
export var getVoterInformationLetterPasswordPolicyError = function (error) {
    if (isPasswordPolicyNotConfiguredError(error)) {
        return "notConfigured";
    }
    if (hasGraphQLActionErrorCode(error, PASSWORD_POLICY_MINIMUM_LENGTH_MISSING_ERROR_CODE)) {
        return "minimumLengthMissing";
    }
    if (hasGraphQLActionErrorCode(error, PASSWORD_POLICY_CHARACTER_CLASS_MISSING_ERROR_CODE)) {
        return "characterClassMissing";
    }
    return undefined;
};
